<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Monitor from "@/services/Monitor";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Swal from "sweetalert2";

export default {
  components: {
    validationMessages,
    vueDropzone: vue2Dropzone,
  },

  props: {
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      competition_name: "",
      competition_type: 10,
      competition_code: "",
      competition_country: "",
      competition_country_code: "",
      competition_logo: "",
      competition_id: "",
      competitionTypeOptions: [
        { value: 10, text: "Domestic league (10)" },
        { value: 20, text: "Domestic cup (20)" },
      ],
      file_e: "",
      showModal: false,
      submitted: false,
      tryingToSubmit: false,
      error: null,
      dropzoneOptions: {
        url: "/",
        autoProcessQueue: false,
        addRemoveLinks: true,
        thumbnailWidth: 200,
        thumbnailMethod: "contain",
        maxFilesize: 5,
        acceptedFiles: "image/*",
        maxFiles: 1,
      },
    };
  },

  validations: {
    competition_name: { required },
    competition_type: { required },
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("add_sport_competition");
    },

    formatError(error) {
      if (!error) return "Failed to create competition";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },
    selectFile(files) {
      setTimeout(() => {
        const dropzone = this.$refs.competitionLogoDropzone;
        if (!dropzone) return;

        const queuedFiles = dropzone.getQueuedFiles();
        if (queuedFiles.length > 1) {
          Swal.fire("Fail!", "You can only upload one image", "warning");
          files.forEach((file) => {
            dropzone.removeFile(file);
          });
          return;
        }
        this.file_e = queuedFiles[0] || "";
      }, 250);
    },

    async createSportCompetition() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const payload = {
        competition_name: this.competition_name,
        competition_type: this.competition_type,
        provider: this.provider,
      };

      if (this.competition_code) payload.competition_code = this.competition_code;
      if (this.competition_country) payload.competition_country = this.competition_country;
      if (this.competition_country_code) payload.competition_country_code = this.competition_country_code;
      if (this.competition_logo) payload.competition_logo = this.competition_logo;
      if (this.file_e) payload.competition_logo = this.file_e;
      if (this.competition_id) payload.competition_id = this.competition_id;

      try {
        await Monitor.createSportCompetition(payload);
        Swal.fire("Success!", "Competition created successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },

    resetForm() {
      this.competition_name = "";
      this.competition_type = 10;
      this.competition_code = "";
      this.competition_country = "";
      this.competition_country_code = "";
      this.competition_logo = "";
      this.file_e = "";
      this.competition_id = "";
      this.submitted = false;
      this.$v.$reset();
    },
  },
};
</script>

<template>
  <b-modal
    id="add_sport_competition"
    title="Add Competition"
    title-class="font-18"
    centered
    size="lg"
    @hidden="resetForm"
  >
    <form @submit.prevent="createSportCompetition">
      <div class="form-group mb-3">
        <label for="competition_name">Competition Name <span class="text-danger">*</span></label>
        <input
          id="competition_name"
          v-model="competition_name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': submitted && $v.competition_name.$error }"
        />
        <validationMessages v-if="submitted" :field="$v.competition_name" />
      </div>

      <div class="form-group mb-3">
        <label for="competition_type">Competition Type <span class="text-danger">*</span></label>
        <b-form-select
          id="competition_type"
          v-model="competition_type"
          :options="competitionTypeOptions"
          :class="{ 'is-invalid': submitted && $v.competition_type.$error }"
        />
        <validationMessages v-if="submitted" :field="$v.competition_type" />
      </div>

      <div class="form-group mb-3">
        <label for="competition_code">Competition Code</label>
        <input id="competition_code" v-model="competition_code" type="text" class="form-control" />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="competition_country">Country</label>
            <input id="competition_country" v-model="competition_country" type="text" class="form-control" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="competition_country_code">Country Code</label>
            <input id="competition_country_code" v-model="competition_country_code" type="text" class="form-control" />
          </div>
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="competition_logo">Logo URL</label>
        <input
          id="competition_logo"
          v-model="competition_logo"
          type="text"
          class="form-control"
          :disabled="!!file_e"
          placeholder="Optional if uploading file below"
        />
      </div>

      <div class="form-group mb-3">
        <label>Competition Logo Upload</label>
        <vue-dropzone
          id="add_competition_logo_dropzone"
          ref="competitionLogoDropzone"
          :use-custom-slot="true"
          :options="dropzoneOptions"
          @vdropzone-files-added="selectFile"
          no-progress-bar
        >
          <div class="dropzone-custom-content">
            <i class="display-4 text-muted bx bxs-cloud-upload"></i>
            <h4>Drop file here or click to upload.</h4>
          </div>
        </vue-dropzone>
      </div>

      <div class="form-group mb-3">
        <label for="competition_id">Competition ID</label>
        <input id="competition_id" v-model="competition_id" type="text" class="form-control" placeholder="Auto-generated if empty" />
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createSportCompetition" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
